import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { ContractPaymentsListRow } from "@/admin/entities/contractPayments";
import { ContractPaymentActions } from "./ContractPaymentActions";
import { ContractPaymentLines } from "./ContractPaymentLines";
import {
  formatDay,
  formatInvoiceConversion,
  formatInvoiceRate,
  formatMoney,
  formatMoneyPair,
} from "./billingFormat";
import {
  ContractPaymentStatusLink,
  OpenContractPaymentButton,
} from "./ContractPaymentCardLinks";

/**
 * Колонки платежа по контракту, общие для страницы «Биллинг» и вкладки
 * организации: рендер и подписи одинаковые, различается только набор
 * столбцов вокруг них (общая страница добавляет «Организация», вкладка
 * добавляет «Период»).
 */

// Колонка номера инвойса вынесена в отдельный файл: ссылка на файл, а не
// текстовый рендер, как у соседних колонок этого модуля. Реэкспорт нужен,
// чтобы вызывающий код обращался к ней через общий неймспейс
// ContractPaymentColumns, как и к остальным колонкам платежа.
export { buildInvoiceNumberColumn } from "./invoiceColumns";

export function buildInvoiceDateColumn(): ColumnDefinition<ContractPaymentsListRow> {
  return {
    name: "invoiceDate",
    label: "Дата инвойса",
    options: {
      customBodyRender: (value: unknown) => formatDay(value as string),
    },
  };
}

export function buildExpiredAtColumn(): ColumnDefinition<ContractPaymentsListRow> {
  return {
    name: "expiredAt",
    label: "Срок оплаты",
    options: {
      customBodyRender: (value: unknown) => formatDay(value as string),
    },
  };
}

export function buildAmountColumn(): ColumnDefinition<ContractPaymentsListRow> {
  return {
    name: "amount",
    label: "Сумма",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const payment = data as ContractPaymentsListRow;
        if (!payment.invoiceAmount) {
          return formatMoneyPair(
            payment.amount,
            payment.amountInOrganizationCurrency
          );
        }
        // Счёт выставлен в другой валюте: первой строкой ставка и сумма к
        // оплате («200 USD → 184,68 EUR»), курс счёта в подсказке, сумма в
        // валюте организации мелко под ними.
        return (
          <Tooltip
            title={formatInvoiceRate(
              payment.amount.currency,
              payment.invoiceExchangeRate,
              payment.invoiceAmount.currency
            )}
          >
            <Stack sx={{ cursor: "help" }}>
              <Typography variant="body2" noWrap>
                {formatInvoiceConversion(payment.amount, payment.invoiceAmount)}
              </Typography>
              {payment.amountInOrganizationCurrency && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {formatMoney(payment.amountInOrganizationCurrency)}
                </Typography>
              )}
            </Stack>
          </Tooltip>
        );
      },
    },
  };
}

export interface ContractPaymentCardLinkOptions {
  /** Путь страницы платежей, если таблица живёт внутри чужого диалога. */
  cardPath?: string;
}

export function buildStatusColumn(
  options: ContractPaymentCardLinkOptions = {}
): ColumnDefinition<ContractPaymentsListRow> {
  return {
    name: "status",
    label: "Статус",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const payment = data as ContractPaymentsListRow;
        return (
          <ContractPaymentStatusLink
            payment={payment}
            cardPath={options.cardPath}
          />
        );
      },
    },
  };
}

export function buildLinesColumn(): ColumnDefinition<ContractPaymentsListRow> {
  return {
    name: "lines",
    label: "Строки",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const payment = data as ContractPaymentsListRow;
        return (
          <Stack sx={{ minWidth: 320 }}>
            <ContractPaymentLines lines={payment.lines} />
          </Stack>
        );
      },
    },
  };
}

// Действия в строке списка остаются без onCompleted: строку обновляет
// нормализованный кеш Apollo по ответу мутации.
export function buildActionsColumn(
  options: ContractPaymentCardLinkOptions = {}
): ColumnDefinition<ContractPaymentsListRow> {
  return {
    name: "actions",
    label: "",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const payment = data as ContractPaymentsListRow;
        return (
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <OpenContractPaymentButton
              paymentId={payment.id}
              cardPath={options.cardPath}
            />
            <ContractPaymentActions payment={payment} />
          </Stack>
        );
      },
    },
  };
}
